/**
 * Internal IA-fit review page (/internal — not linked from public navigation).
 *
 * IA staff sign in with their Google account; the backend verifies the ID
 * token and only allowlisted IA emails/domains may view the IA-fit
 * assessments. Founders never see this data.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  GOOGLE_CLIENT_ID,
  AnalysisBlock,
  PitchDetail,
  PitchSummary,
  getAdminToken,
  getPitch,
  listPitches,
  setAdminToken,
  whoami,
} from '../services/adminApi';

declare global {
  interface Window {
    google?: any;
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

const fitColor = (score: number | null): 'success' | 'warning' | 'error' | 'default' => {
  if (score == null) return 'default';
  if (score >= 70) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
};

const ScoreChip: React.FC<{ score: number | null; max: number }> = ({ score, max }) => (
  <Chip
    size="small"
    color={fitColor(score == null ? null : (score / max) * 100)}
    label={score == null ? '—' : `${score}/${max}`}
  />
);

const AnalysisSection: React.FC<{ title: string; analysis: AnalysisBlock }> = ({ title, analysis }) => {
  const lists: Array<[string, string[] | undefined]> = [
    ['Key strengths', analysis.key_strengths],
    ['Key concerns', analysis.key_concerns || analysis.key_weaknesses],
    ['Recommendations', analysis.recommendations],
    ['Next steps', analysis.next_steps],
  ];
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {analysis.overall_analysis && (
        <Typography variant="body2" sx={{ mb: 2 }}>{analysis.overall_analysis}</Typography>
      )}
      {analysis.category_scores && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            {Object.entries(analysis.category_scores).map(([key, cat]) => (
              <TableRow key={key}>
                <TableCell sx={{ width: 220, fontWeight: 600 }}>{cat.display_name || key}</TableCell>
                <TableCell sx={{ width: 90 }}>
                  <ScoreChip score={cat.score} max={cat.max_score} />
                </TableCell>
                <TableCell>{cat.reasoning}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {lists.map(([label, items]) =>
        items && items.length > 0 ? (
          <Box key={label} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2">{label}</Typography>
            <ul style={{ marginTop: 4 }}>
              {items.map((item, i) => (
                <li key={i}><Typography variant="body2">{item}</Typography></li>
              ))}
            </ul>
          </Box>
        ) : null
      )}
    </Box>
  );
};

const InternalReview: React.FC = () => {
  const [staffEmail, setStaffEmail] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [pitches, setPitches] = useState<PitchSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState<'created_at' | 'ia_fit_score' | 'general_score'>('created_at');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<PitchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const signOut = useCallback((message?: string) => {
    setAdminToken(null);
    setStaffEmail(null);
    if (message) setAuthError(message);
  }, []);

  const loadPitches = useCallback(async (order: typeof orderBy) => {
    setLoading(true);
    try {
      const data = await listPitches({ order_by: order, limit: 100 });
      setPitches(data.items);
      setTotal(data.total);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        signOut('Your sign-in expired. Please sign in again.');
      } else {
        setAuthError(err?.response?.data?.detail || 'Failed to load pitches.');
      }
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  const handleCredential = useCallback(async (response: { credential: string }) => {
    setAuthError(null);
    setAdminToken(response.credential);
    try {
      const email = await whoami();
      setStaffEmail(email);
    } catch (err: any) {
      const status = err?.response?.status;
      setAdminToken(null);
      if (status === 403) {
        setAuthError('This Google account is not authorized for internal review.');
      } else if (status === 503) {
        setAuthError('Internal review is not configured on the server (GOOGLE_OAUTH_CLIENT_ID missing).');
      } else {
        setAuthError(err?.response?.data?.detail || 'Sign-in failed. Please try again.');
      }
    }
  }, []);

  // Resume an existing session, or render the Google sign-in button
  useEffect(() => {
    if (getAdminToken()) {
      whoami()
        .then(setStaffEmail)
        .catch(() => setAdminToken(null));
    }
  }, []);

  useEffect(() => {
    if (staffEmail) return;
    if (!GOOGLE_CLIENT_ID) {
      setAuthError('REACT_APP_GOOGLE_CLIENT_ID is not set — internal review is unavailable in this build.');
      return;
    }
    const renderButton = () => {
      if (!window.google || !buttonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredential,
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });
    };
    if (window.google) {
      renderButton();
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = renderButton;
    document.head.appendChild(script);
  }, [staffEmail, handleCredential]);

  useEffect(() => {
    if (staffEmail) {
      loadPitches(orderBy);
    }
  }, [staffEmail, orderBy, loadPitches]);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      setDetail(await getPitch(id));
    } catch {
      setAuthError('Failed to load pitch detail.');
    } finally {
      setDetailLoading(false);
    }
  };

  if (!staffEmail) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          Internal Review
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          International Accelerator staff only. Sign in with your IA Google account.
        </Typography>
        {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}
        <Box ref={buttonRef} sx={{ display: 'flex', justifyContent: 'center' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>IA Fit Review</Typography>
          <Typography variant="body2" color="text.secondary">
            {total} pitches · signed in as {staffEmail}
          </Typography>
        </Box>
        <Select
          size="small"
          value={orderBy}
          onChange={(e) => setOrderBy(e.target.value as typeof orderBy)}
        >
          <MenuItem value="created_at">Newest first</MenuItem>
          <MenuItem value="ia_fit_score">IA fit score</MenuItem>
          <MenuItem value="general_score">General score</MenuItem>
        </Select>
      </Box>

      {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Deck</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Industry / Stage</TableCell>
              <TableCell align="center">General</TableCell>
              <TableCell align="center">IA Fit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pitches.map((p) => (
              <TableRow
                key={p.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => openDetail(p.id)}
              >
                <TableCell>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell>{p.filename || '—'}</TableCell>
                <TableCell>{p.contact_email || '—'}</TableCell>
                <TableCell>{p.country || '—'}</TableCell>
                <TableCell>{[p.industry, p.stage].filter(Boolean).join(' / ') || '—'}</TableCell>
                <TableCell align="center">
                  {p.general_score != null ? `${p.general_score}/10` : '—'}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    color={fitColor(p.ia_fit_score)}
                    label={p.ia_fit_score != null ? `${p.ia_fit_score}/100` : 'n/a'}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!loading && pitches.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No analyzed pitches yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detail !== null || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detailLoading && <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>}
        {detail && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {detail.filename || 'Pitch'}
              <Typography variant="body2" color="text.secondary">
                {detail.contact_email} · {detail.country} ·{' '}
                {detail.created_at ? new Date(detail.created_at).toLocaleString() : ''}
              </Typography>
              <IconButton
                onClick={() => setDetail(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <Chip
                  color={fitColor(detail.ia_fit_score)}
                  label={`IA Fit: ${detail.ia_fit_score != null ? `${detail.ia_fit_score}/100` : 'n/a'}`}
                />
                <Chip
                  variant="outlined"
                  label={`General: ${detail.general_score != null ? `${detail.general_score}/10` : 'n/a'}`}
                />
                {detail.ia_fit_score != null && (
                  <Chip
                    variant="outlined"
                    color={detail.is_good_fit ? 'success' : 'default'}
                    label={detail.is_good_fit ? 'Good fit' : 'Not a fit (yet)'}
                  />
                )}
              </Box>
              <AnalysisSection title="IA Fit Assessment" analysis={detail.ia_fit_analysis} />
              <Divider sx={{ my: 2 }} />
              <AnalysisSection title="General Analysis (shown to founder)" analysis={detail.general_analysis} />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default InternalReview;
